/**
 * Pure Practice-mode persistence layer.
 *
 * Owns the on-disk shape of `castoffpro.gamification.practice.v1` and the
 * conversions to/from in-memory {@link ManeuverDeclaration} +
 * {@link ManeuverResult} pairs. No Angular, no signals — the
 * `practice.store` wraps this and exposes reactive accessors.
 *
 * Design choices:
 *   - FIFO history with a hard cap (default 50) so the JSON blob can't grow
 *     unbounded across thousands of practice runs.
 *   - `lastSelections` lets the setup screen prefill the previously-used
 *     boat / terrain / tier when reopening Practice.
 *   - Per the plan's Practice MVP scope: NO badge unlocks are stored here.
 *     Badges land in the Career profile blob (Etap 2).
 *   - All read/write goes through {@link StorageLike} so tests can inject
 *     an in-memory stub and Safari private-mode is tolerated at the
 *     callsite of {@link safeStorage}.
 */

import type { StorageLike } from "../storage/migrate-vf-to-castoffpro";
import type { DifficultyTierId, ManeuverCategory } from "./gamification.types";
import type {
  ManeuverDeclaration,
  ManeuverResult,
} from "./maneuver-state-machine";

/** Hard cap on retained history entries — FIFO drops oldest. */
export const PRACTICE_HISTORY_CAP = 50;

/** Schema version embedded in the stored blob. Bump when shape changes. */
export const PRACTICE_STATE_VERSION = 1;

/** A single completed/failed/aborted Practice session entry. */
export interface PracticeSessionEntry {
  readonly maneuverId: string;
  readonly category: ManeuverCategory;
  readonly tier: DifficultyTierId;
  readonly boatId: string;
  readonly terrainId: string;
  /** ms since epoch when the run finished. */
  readonly timestampMs: number;
  readonly elapsedSec: number;
  /** Outcome label — drives badge filtering in Career (no-op for Practice). */
  readonly status: "completed" | "failed" | "aborted";
  /** Final XP if scoring ran, else `null` (aborted runs skip scoring). */
  readonly finalScore: number | null;
  /** Reason a failed/aborted run terminated, if any. */
  readonly failureReason:
    | "severe_collision"
    | "time_limit"
    | "aborted_by_user"
    | null;
  readonly hadCollision: boolean;
  readonly hadDrag: boolean;
  readonly maxContactSpeedMs: number;
}

/** Last-used setup choices for the Practice setup screen. */
export interface PracticeLastSelections {
  readonly maneuverId: string | null;
  readonly tier: DifficultyTierId | null;
  readonly boatId: string | null;
  readonly terrainId: string | null;
}

export interface PracticeState {
  readonly history: readonly PracticeSessionEntry[];
  readonly lastSelections: PracticeLastSelections;
}

export const EMPTY_PRACTICE_STATE: PracticeState = {
  history: [],
  lastSelections: {
    maneuverId: null,
    tier: null,
    boatId: null,
    terrainId: null,
  },
};

interface PersistedBlob {
  readonly version: number;
  readonly history: readonly PracticeSessionEntry[];
  readonly lastSelections: PracticeLastSelections;
}

/**
 * Build a history entry from a finished maneuver. The state machine's
 * `result` is required — callers should only call this once a terminal
 * phase has been reached.
 */
export function entryFromResult(
  declaration: ManeuverDeclaration,
  result: ManeuverResult,
  timestampMs: number = Date.now(),
): PracticeSessionEntry {
  return {
    maneuverId: declaration.def.id,
    category: declaration.def.category,
    tier: declaration.tier,
    boatId: declaration.boatId,
    terrainId: declaration.terrainId,
    timestampMs,
    elapsedSec: result.elapsedSec,
    status: result.phase,
    finalScore: result.breakdown?.finalScore ?? null,
    failureReason: result.failureReason,
    hadCollision: result.window.hadCollision,
    hadDrag: result.window.hadDrag,
    maxContactSpeedMs: result.window.maxContactSpeedMs,
  };
}

/**
 * Append an entry to the state's history, dropping the oldest when the
 * cap is exceeded. Returns a new state — does not mutate the input.
 */
export function appendHistory(
  state: PracticeState,
  entry: PracticeSessionEntry,
  cap: number = PRACTICE_HISTORY_CAP,
): PracticeState {
  const next = [...state.history, entry];
  const trimmed = next.length > cap ? next.slice(next.length - cap) : next;
  return { ...state, history: trimmed };
}

/** Patch the `lastSelections` field, leaving history alone. */
export function patchLastSelections(
  state: PracticeState,
  patch: Partial<PracticeLastSelections>,
): PracticeState {
  return {
    ...state,
    lastSelections: { ...state.lastSelections, ...patch },
  };
}

/**
 * Parse a previously-persisted blob. Tolerant of missing fields and bad
 * shapes — anything unrecognised falls back to {@link EMPTY_PRACTICE_STATE}
 * so a single corrupt run never wipes out the player's setup defaults.
 */
export function parsePracticeBlob(raw: string | null): PracticeState {
  if (raw == null || raw.length === 0) return EMPTY_PRACTICE_STATE;
  let blob: unknown;
  try {
    blob = JSON.parse(raw);
  } catch {
    return EMPTY_PRACTICE_STATE;
  }
  if (typeof blob !== "object" || blob === null) return EMPTY_PRACTICE_STATE;
  const obj = blob as Partial<PersistedBlob>;
  const history = Array.isArray(obj.history)
    ? obj.history.filter(isValidEntry)
    : [];
  const lastSelections = isValidSelections(obj.lastSelections)
    ? obj.lastSelections
    : EMPTY_PRACTICE_STATE.lastSelections;
  return { history, lastSelections };
}

/** Serialise the state for storage. */
export function serializePracticeBlob(state: PracticeState): string {
  const blob: PersistedBlob = {
    version: PRACTICE_STATE_VERSION,
    history: state.history,
    lastSelections: state.lastSelections,
  };
  return JSON.stringify(blob);
}

/**
 * Read the state from the given storage. Always returns a valid state;
 * storage failures (quota, private mode) collapse to {@link EMPTY_PRACTICE_STATE}.
 */
export function loadPracticeState(
  storage: StorageLike | null,
  key: string,
): PracticeState {
  if (storage === null) return EMPTY_PRACTICE_STATE;
  try {
    return parsePracticeBlob(storage.getItem(key));
  } catch {
    return EMPTY_PRACTICE_STATE;
  }
}

/**
 * Persist the state to the given storage. Silently swallows write errors
 * (Safari private mode, quota) — the in-memory copy stays authoritative
 * for the rest of the session.
 */
export function savePracticeState(
  storage: StorageLike | null,
  key: string,
  state: PracticeState,
): void {
  if (storage === null) return;
  try {
    storage.setItem(key, serializePracticeBlob(state));
  } catch {
    // Quota or access errors are non-fatal.
  }
}

// ----- internal validators --------------------------------------------------

function isValidEntry(value: unknown): value is PracticeSessionEntry {
  if (typeof value !== "object" || value === null) return false;
  const e = value as Partial<PracticeSessionEntry>;
  return (
    typeof e.maneuverId === "string" &&
    typeof e.category === "string" &&
    typeof e.tier === "string" &&
    typeof e.boatId === "string" &&
    typeof e.terrainId === "string" &&
    typeof e.timestampMs === "number" &&
    typeof e.elapsedSec === "number" &&
    (e.status === "completed" ||
      e.status === "failed" ||
      e.status === "aborted") &&
    (e.finalScore === null || typeof e.finalScore === "number") &&
    typeof e.hadCollision === "boolean" &&
    typeof e.hadDrag === "boolean" &&
    typeof e.maxContactSpeedMs === "number"
  );
}

function isValidSelections(value: unknown): value is PracticeLastSelections {
  if (typeof value !== "object" || value === null) return false;
  const s = value as Partial<PracticeLastSelections>;
  return (
    (s.maneuverId === null || typeof s.maneuverId === "string") &&
    (s.tier === null || typeof s.tier === "string") &&
    (s.boatId === null || typeof s.boatId === "string") &&
    (s.terrainId === null || typeof s.terrainId === "string")
  );
}
